import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import { isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";
import { hrmsSuccessMessage } from "../utils/hrmsSuccessMessage";
import { formatInr, formatMonthLabel } from "../utils/hrmsMoney";
import { HrmsBranchSelect } from "./HrmsBranchSelect";

function pickFirst(obj, keys, fallback = null) {
  if (!obj || typeof obj !== "object") return fallback;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
}

function mapPayrollEmployeeRow(r) {
  const name = pickFirst(r, ["user_name", "employee_name", "name"], "—") || "—";
  const ua = pickFirst(r, ["user_admin_id", "user_adminId"], null);
  const base = pickFirst(r, [
    "prorated_base",
    "base_monthly_salary",
    "base_salary",
    "base",
  ]);
  const inc = pickFirst(r, [
    "incentives_total",
    "incentive_total",
    "incentives",
    "total_incentives",
  ]);
  const ot = pickFirst(r, ["overtime_total", "overtime", "total_overtime"]);
  const adv = pickFirst(r, [
    "advances_total",
    "advance_total",
    "advances",
    "total_advances",
  ]);
  const net = pickFirst(r, ["net_salary", "net_pay", "net", "net_amount"]);
  const gross = pickFirst(r, ["gross", "gross_pay", "gross_salary"]);
  const ded = pickFirst(r, [
    "deductions_total",
    "deductions",
    "total_deductions",
  ]);
  const locked = pickFirst(r, ["locked", "is_locked", "isLocked"], 0);
  const runStatus = pickFirst(r, ["run_status", "runStatus"], "");
  return {
    raw: r,
    user_admin_id: ua != null ? Number(ua) : null,
    name: String(name),
    base,
    inc,
    ot,
    adv,
    net,
    gross,
    ded,
    locked: Number(locked) === 1 || locked === true,
    runStatus: String(runStatus || ""),
  };
}

export function PayrollTab({
  branchId,
  onBranchChange,
  branchOptions,
  branchesLoading,
}) {
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const canRun = hasPermission(PERMISSIONS.HRMS_PAYROLL_RUN);
  const canView = hasPermission(PERMISSIONS.HRMS_PAYROLL_VIEW);
  const canLock = hasPermission(PERMISSIONS.HRMS_PAYROLL_LOCK);

  const [payMonth, setPayMonth] = useState(() =>
    new Date().toISOString().slice(0, 7),
  );

  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [registerRows, setRegisterRows] = useState([]);
  const [registerLoading, setRegisterLoading] = useState(false);

  const [genOpen, setGenOpen] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [lockOpen, setLockOpen] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockBusy, setUnlockBusy] = useState(false);

  const [slipOpen, setSlipOpen] = useState(false);
  const [slipLoading, setSlipLoading] = useState(false);
  const [slipTitle, setSlipTitle] = useState("");
  const [slipPayload, setSlipPayload] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    setPreviewRows([]);
    setRegisterRows([]);
  }, [branchId, payMonth]);

  const loadPreview = useCallback(async () => {
    if (!branchId || !canRun) {
      setPreviewRows([]);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await hrmsApi("payroll_preview", {
        branch_id: Number(branchId),
        month: String(payMonth).trim(),
      });
      if (isErpApiSuccess(res) && res.data) {
        const arr = Array.isArray(res.data.employees) ? res.data.employees : [];
        setPreviewRows(arr.map(mapPayrollEmployeeRow));
      } else {
        setPreviewRows([]);
        if (res?.success === false && res?.message) toast.error(res.message);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load payroll preview.");
    } finally {
      setPreviewLoading(false);
    }
  }, [branchId, payMonth, canRun]);

  const loadRegister = useCallback(async () => {
    if (!branchId || !canView) {
      setRegisterRows([]);
      return;
    }
    setRegisterLoading(true);
    try {
      const res = await hrmsApi("payroll_register", {
        branch_id: Number(branchId),
        month: String(payMonth).trim(),
      });
      if (isErpApiSuccess(res) && res.data) {
        const arr = Array.isArray(res.data.employees) ? res.data.employees : [];
        setRegisterRows(arr.map(mapPayrollEmployeeRow));
      } else {
        setRegisterRows([]);
        if (res?.success === false && res?.message) toast.error(res.message);
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load payroll register.");
    } finally {
      setRegisterLoading(false);
    }
  }, [branchId, payMonth, canView]);

  useEffect(() => {
    if (canRun) void loadPreview();
  }, [loadPreview, canRun]);

  useEffect(() => {
    if (canView) void loadRegister();
  }, [loadRegister, canView]);

  const runGenerate = async () => {
    if (!canRun || !branchId) return;
    setGenBusy(true);
    try {
      const res = await hrmsApi("payroll_generate", {
        branch_id: Number(branchId),
        month: String(payMonth).trim(),
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(
            res,
            `Payroll generated for ${formatMonthLabel(payMonth)}.`,
          ),
        );
        setGenOpen(false);
        await loadPreview();
        await loadRegister();
      } else {
        toast.error(res?.message || "Generate failed.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not generate payroll.");
    } finally {
      setGenBusy(false);
    }
  };

  const runLock = async () => {
    if (!canLock || !branchId) return;
    setLockBusy(true);
    try {
      const res = await hrmsApi("payroll_lock", {
        branch_id: Number(branchId),
        month: String(payMonth).trim(),
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(
            res,
            `Payroll locked for ${formatMonthLabel(payMonth)}.`,
          ),
        );
        setLockOpen(false);
        await loadRegister();
        await loadPreview();
      } else {
        toast.error(res?.message || "Lock failed.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not lock payroll.");
    } finally {
      setLockBusy(false);
    }
  };

  const runUnlock = async () => {
    if (!canLock || !branchId) return;
    setUnlockBusy(true);
    try {
      const res = await hrmsApi("payroll_unlock", {
        branch_id: Number(branchId),
        month: String(payMonth).trim(),
      });
      if (isErpApiSuccess(res)) {
        toast.success(
          hrmsSuccessMessage(
            res,
            `Payroll unlocked for ${formatMonthLabel(payMonth)}.`,
          ),
        );
        setUnlockOpen(false);
        await loadRegister();
        await loadPreview();
      } else {
        toast.error(res?.message || "Unlock failed.");
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not unlock payroll.");
    } finally {
      setUnlockBusy(false);
    }
  };

  const openPayslip = async (row) => {
    if (!canView || !branchId || !row.user_admin_id) return;
    setSlipOpen(true);
    setSlipLoading(true);
    setSlipTitle(row.name);
    setSlipPayload(null);
    try {
      const res = await hrmsApi("payroll_payslip", {
        branch_id: Number(branchId),
        user_admin_id: Number(row.user_admin_id),
        month: String(payMonth).trim(),
      });
      if (isErpApiSuccess(res) && res.data?.payslip) {
        setSlipPayload(res.data.payslip);
      } else {
        setSlipPayload(null);
        toast.error(res?.message || "No payslip data.");
      }
    } catch (err) {
      setSlipPayload(null);
      hrmsErrorToast(err, "Could not load payslip.");
    } finally {
      setSlipLoading(false);
    }
  };

  const handlePrint = () => {
    try {
      const w = window.open("", "_blank");
      if (!w || !printRef.current) {
        window.print();
        return;
      }
      w.document.write(
        `<html><head><title>Payslip</title></head><body>${printRef.current.innerHTML}</body></html>`,
      );
      w.document.close();
      w.focus();
      w.print();
      w.close();
    } catch {
      window.print();
    }
  };

  const flowCards = useMemo(
    () => [
      {
        key: "preview",
        title: "Preview payroll",
        icon: "solar:eye-bold",
        desc: "Review computed totals before posting.",
        show: canRun,
        onClick: () => {
          void loadPreview();
        },
      },
      {
        key: "generate",
        title: "Generate payroll",
        icon: "solar:calculator-minimalistic-bold",
        desc: "Persist draft payroll rows for the month.",
        show: canRun,
        onClick: () => setGenOpen(true),
      },
      {
        key: "register",
        title: "Review register",
        icon: "solar:clipboard-list-bold",
        desc: "Gross, deductions, net, lock status.",
        show: canView,
        onClick: () => {
          void loadRegister();
        },
      },
      {
        key: "lock",
        title: "Lock payroll",
        icon: "solar:lock-keyhole-bold",
        desc: "Finalize month and apply advances.",
        show: canLock,
        onClick: () => setLockOpen(true),
      },
      {
        key: "payslip",
        title: "Payslip",
        icon: "solar:bill-list-bold",
        desc: "Open from register: View payslip on a row.",
        show: canView,
        onClick: () => {
          void loadRegister();
          toast.success(
            "Select an employee in the register, then click View payslip on that row.",
          );
        },
      },
    ],
    [canRun, canView, canLock, loadPreview, loadRegister],
  );

  const anyAccess = canRun || canView || canLock;

  return (
    <div className="row g-3">
      <div className="col-12 d-flex flex-wrap align-items-end gap-3">
        <HrmsBranchSelect
          branchId={branchId}
          onChangeBranchId={onBranchChange}
          branchOptions={branchOptions}
          loading={branchesLoading}
          buttonTableStyle={buttonTableStyle}
        />
        <div>
          <Form.Control
            type="month"
            value={payMonth}
            onChange={(ev) => setPayMonth(ev.target.value)}
            disabled={!anyAccess}
            aria-label="Pay month"
            title="Pay month"
            style={{ minWidth: 160, minHeight: 44 }}
          />
        </div>
        {canLock && (
          <Button
            type="button"
            variant="outline-secondary"
            className={buttonClass}
            onClick={() => setUnlockOpen(true)}
          >
            <Icon icon="solar:lock-unlocked-bold" className="me-1" width={18} />
            Unlock month
          </Button>
        )}
      </div>

      {!anyAccess ? (
        <div className="col-12 text-center text-muted py-4">
          You do not have permission to access payroll.
        </div>
      ) : (
        <>
          <div className="col-12">
            <Row className="g-3">
              {flowCards
                .filter((c) => c.show)
                .map((c) => (
                  <Col key={c.key} xs={12} sm={6} lg={4} xl>
                    <Card
                      className={`h-100 ${cardClass} ${
                        buttonTableStyle === "square" ? "rounded-0" : ""
                      } shadow-sm`}
                    >
                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Icon icon={c.icon} width={26} height={26} />
                          <Card.Title className="mb-0 fs-5">
                            {c.title}
                          </Card.Title>
                        </div>
                        <Card.Text className="text-muted small flex-grow-1">
                          {c.desc}
                        </Card.Text>
                        <Button
                          type="button"
                          variant={c.key === "lock" ? "danger" : "primary"}
                          className={`mt-auto ${buttonClass}`}
                          onClick={c.onClick}
                          disabled={!branchId}
                        >
                          Open
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
            </Row>
          </div>

          {canRun && (
            <div className="col-12" id="hrms-payroll-preview-block">
              <h5 className="mb-3">Payroll preview</h5>
              <div
                className={`table-responsive border ${cardClass} ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
              >
                <table
                  className={`table text-nowrap mb-0 align-middle ${tableClass} ${
                    buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
                >
                  <thead className="text-dark fs-4">
                    <tr>
                      <th>Employee</th>
                      <th className="text-end">Base salary</th>
                      <th className="text-end">Incentives</th>
                      <th className="text-end">Overtime</th>
                      <th className="text-end">Advances</th>
                      <th className="text-end">Net salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.length > 0 ? (
                      previewRows.map((r, i) => (
                        <tr key={`${r.user_admin_id ?? i}-${i}`}>
                          <td>{r.name}</td>
                          <td className="text-end">{formatInr(r.base)}</td>
                          <td className="text-end">{formatInr(r.inc)}</td>
                          <td className="text-end">{formatInr(r.ot)}</td>
                          <td className="text-end">{formatInr(r.adv)}</td>
                          <td className="text-end fw-semibold">
                            {formatInr(r.net)}
                          </td>
                        </tr>
                      ))
                    ) : !previewLoading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          No preview data. Pick branch and month, then refresh
                          using Preview card.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <div className="mt-2">
                <Button
                  type="button"
                  variant="success"
                  className={buttonClass}
                  disabled={!branchId || !previewRows.length}
                  onClick={() => setGenOpen(true)}
                >
                  Generate payroll
                </Button>
              </div>
            </div>
          )}

          {canView && (
            <div className="col-12" id="hrms-payroll-register-block">
              <h5 className="mb-3">
                Payroll register — {formatMonthLabel(payMonth)}
              </h5>
              <div
                className={`table-responsive border ${cardClass} ${
                  buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
              >
                <table
                  className={`table text-nowrap mb-0 align-middle ${tableClass} ${
                    buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
                >
                  <thead className="text-dark fs-4">
                    <tr>
                      <th>Employee</th>
                      <th className="text-end">Gross</th>
                      <th className="text-end">Deductions</th>
                      <th className="text-end">Net</th>
                      <th>Locked</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registerRows.length > 0 ? (
                      registerRows.map((r, i) => (
                        <tr key={`${r.user_admin_id ?? i}-reg-${i}`}>
                          <td>{r.name}</td>
                          <td className="text-end">
                            {formatInr(r.gross ?? r.base)}
                          </td>
                          <td className="text-end">{formatInr(r.ded)}</td>
                          <td className="text-end fw-semibold">
                            {formatInr(r.net)}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                r.locked
                                  ? "bg-success-subtle text-success"
                                  : "bg-secondary-subtle text-secondary"
                              }`}
                            >
                              {r.locked ? "Locked" : "Open"}
                            </span>
                          </td>
                          <td>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline-primary"
                              className={`me-1 ${buttonClass}`}
                              onClick={() => openPayslip(r)}
                            >
                              View payslip
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : !registerLoading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          No register rows. Run payroll generate first.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <Modal
        show={genOpen}
        onHide={() => !genBusy && setGenOpen(false)}
        centered
      >
        <Modal.Header closeButton={!genBusy}>
          <Modal.Title>Generate payroll</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Generate draft payroll for{" "}
          <strong>{formatMonthLabel(payMonth)}</strong> at this branch? If any
          row is already locked for this month, the server may return{" "}
          <strong>409 Conflict</strong>.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={genBusy}
            onClick={() => setGenOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            className={buttonClass}
            disabled={genBusy}
            onClick={runGenerate}
          >
            {genBusy ? "Working…" : "Generate"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={lockOpen}
        onHide={() => !lockBusy && setLockOpen(false)}
        centered
      >
        <Modal.Header closeButton={!lockBusy}>
          <Modal.Title>Lock payroll month</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Lock payroll for <strong>{formatMonthLabel(payMonth)}</strong>? This
          applies advance deductions as per server rules.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={lockBusy}
            onClick={() => setLockOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            className={buttonClass}
            disabled={lockBusy}
            onClick={runLock}
          >
            {lockBusy ? "Locking…" : "Lock"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={unlockOpen}
        onHide={() => !unlockBusy && setUnlockOpen(false)}
        centered
      >
        <Modal.Header closeButton={!unlockBusy}>
          <Modal.Title>Unlock payroll month</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Unlock payroll for <strong>{formatMonthLabel(payMonth)}</strong>?
          Draft state and advance reversals are handled by the server.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            disabled={unlockBusy}
            onClick={() => setUnlockOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            className={buttonClass}
            disabled={unlockBusy}
            onClick={runUnlock}
          >
            {unlockBusy ? "Unlocking…" : "Unlock"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={slipOpen}
        onHide={() => setSlipOpen(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Payslip — {slipTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {slipLoading ? null : slipPayload &&
            typeof slipPayload === "object" ? (
            <div ref={printRef} className="payslip-print-area">
              <PayslipBody data={slipPayload} payMonth={payMonth} />
            </div>
          ) : (
            <div className="text-muted">No payslip to display.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setSlipOpen(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            className={buttonClass}
            disabled={slipLoading || !slipPayload}
            onClick={handlePrint}
          >
            <Icon icon="solar:printer-bold" className="me-1" width={18} />
            Print
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

const PAYSLIP_HIDDEN_KEYS = new Set(["id", "user_admin_id"]);

function isPayslipAmountField(key) {
  const k = String(key).toLowerCase();
  if (
    k.endsWith("_id") ||
    k === "locked" ||
    k.includes("status") ||
    k.includes("count")
  ) {
    return false;
  }
  return (
    k.includes("salary") ||
    k.includes("deduction") ||
    k.includes("incentive") ||
    k.includes("overtime") ||
    k.includes("advance") ||
    k.includes("gross") ||
    k === "net" ||
    k.endsWith("_net") ||
    k === "base" ||
    k === "prorated_base" ||
    k.endsWith("_amount") ||
    k === "amount"
  );
}

function formatPayslipValue(key, value) {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" && isPayslipAmountField(key)) {
    return formatInr(value);
  }
  return String(value);
}

function PayslipBody({ data, payMonth }) {
  const entries = Object.entries(data).filter(
    ([k, v]) =>
      !PAYSLIP_HIDDEN_KEYS.has(k) &&
      v != null &&
      (typeof v === "string" ||
        typeof v === "number" ||
        typeof v === "boolean"),
  );
  return (
    <div>
      <p className="text-muted small mb-3">
        Pay month: <strong>{formatMonthLabel(payMonth)}</strong>
      </p>
      <Row className="g-3">
        {entries.map(([k, v]) => (
          <Col xs={12} sm={6} key={k}>
            <div className="small text-muted text-uppercase">
              {k.replace(/_/g, " ")}
            </div>
            <div className="fw-semibold">{formatPayslipValue(k, v)}</div>
          </Col>
        ))}
      </Row>
      {typeof data === "object" &&
        data.breakdown &&
        typeof data.breakdown === "object" && (
          <div className="mt-3">
            <h6>Breakdown</h6>
            <pre className="small bg-light p-2 rounded mb-0">
              {JSON.stringify(data.breakdown, null, 2)}
            </pre>
          </div>
        )}
    </div>
  );
}
